// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Hook to check module access based on user's active membership
 * 
 * Simplified for athletes:
 * - athlete_free: Only access to profile, qr-flyer
 * - Any other active athlete membership: Access to all modules
 * 
 * When parent is viewing athlete, uses athlete's membership instead of parent's
 */
export function useMembershipAccess(): {
  hasAccessTo: (moduleSlug: string) => boolean;
  loading: boolean;
  reload: () => void;
} {
  const { profile } = useAuth();
  
  // Try to get athlete context, but don't fail if not available
  let athleteProfileId: string | null = null;
  try {
    const { useAthleteView } = require('@/contexts/AthleteViewContext');
    const athleteContext = useAthleteView();
    athleteProfileId = athleteContext?.athleteProfileId || null;
  } catch (error) {
    athleteProfileId = null;
  }
  
  const [loading, setLoading] = useState(true);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [athleteProfile, setAthleteProfile] = useState<any>(null);

  const reload = () => {
    setReloadTrigger(prev => prev + 1);
  };

  useEffect(() => {
    const fetchMembershipAccess = async () => {
      try {
        setLoading(true);
        
        // If parent is viewing athlete, fetch the athlete's profile
        if (athleteProfileId) {
          const { data: athleteProfileData, error } = await supabase
            .from('profiles')
            .select('id, name, active_membership_slug, membership_level')
            .eq('id', athleteProfileId)
            .single();
            
          if (error) {
            setAthleteProfile(null);
          } else {
            setAthleteProfile(athleteProfileData);
          }
        } else {
          setAthleteProfile(null);
        }

        setLoading(false);
      } catch (err) {
        setAthleteProfile(null);
        setLoading(false);
      }
    };

    fetchMembershipAccess();
  }, [athleteProfileId, profile?.id, reloadTrigger]);

  /**
   * Check if a specific module is allowed based on user's membership
   */
  const hasAccessTo = (moduleSlug: string): boolean => {
    // If still loading, default to false (fail closed)
    if (loading) {
      return false;
    }

    // Determine which profile to use for membership check
    const effectiveProfile = athleteProfile || profile;

    // If no active membership, no modules are allowed
    if (!effectiveProfile?.active_membership_slug) {
      return false;
    }

    const membershipSlug = effectiveProfile.active_membership_slug;

    // Simplified logic for athletes
    if (membershipSlug === 'athlete_free') {
      const allowedModules = ['profile', 'qr-flyer'];
      return allowedModules.includes(moduleSlug);
    }

    // Any other active athlete membership gets access to all modules
    return true;
  };

  return {
    hasAccessTo,
    loading,
    reload,
  };
}